import React, { useEffect, useState, useCallback } from "react";
import toast from "react-hot-toast";
import DashboardLayout from "../components/Nav";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import {
  Pencil,
  Trash2,
  Plus,
  Phone,
  User,
  MapPin,
  CreditCard,
  XCircle,
  Save,
  Search,
  RefreshCcw,
  Mic,
  MicOff,
  Voicemail,
  Server,
  Key,
  FileText,
  ArrowLeft,
  ArrowRight,
  Loader2,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
} from "lucide-react";
import getClientConfig from "../config";

const { API_BASE_URL } = getClientConfig();

export default function VoiceClientPage() {
  useEffect(() => {
    document.title = "Voice Clients | IP Intelligence Portal";
  }, []);

  const token = localStorage.getItem("auth_token");

  // State
  const [clients, setClients] = useState([]);
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingServers, setLoadingServers] = useState(true);

  // Pagination & Search
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(20);

  // Modals
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [form, setForm] = useState({
    issabel_server_id: "",
    name: "",
    extension: "",
    secret: "",
    external_id: "",
    cnic: "",
    address: "",
    li_enabled: false,
    voicemail_enabled: false,
  });

  const [showSecret, setShowSecret] = useState(false);

  // Fetch servers
  useEffect(() => {
    setLoadingServers(true);
    fetch(`${API_BASE_URL}/voice/servers`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        const serverList = data.data || data || [];
        setServers(serverList);
        if (serverList.length === 1) {
          setForm((prev) => ({
            ...prev,
            issabel_server_id: serverList[0].id.toString(),
          }));
        }
        setLoadingServers(false);
      })
      .catch(() => {
        toast.error("Failed to load servers");
        setLoadingServers(false);
      });
  }, [token]);

  // Fetch clients
  const loadClients = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({
      page: currentPage.toString(),
      page_size: pageSize.toString(),
    });
    if (search) params.append("search", search);

    fetch(`${API_BASE_URL}/voice/clients?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        setClients(data.data || []);
        setTotalCount(data.total || 0);
        setLoading(false);
      })
      .catch(() => {
        toast.error("Failed to load clients");
        setLoading(false);
      });
  }, [token, currentPage, pageSize, search]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  // Search handler with debounce
  const handleSearch = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  // Open add modal
  const openAddModal = () => {
    setSelected(null);
    setForm({
      issabel_server_id: servers.length === 1 ? servers[0].id.toString() : "",
      name: "",
      extension: "",
      secret: "",
      external_id: "",
      cnic: "",
      address: "",
      li_enabled: false,
      voicemail_enabled: false,
    });
    setShowSecret(false);
    setModalOpen(true);
  };

  // Open edit modal
  const openEditModal = (client) => {
    setSelected(client);
    setForm({
      issabel_server_id: client.issabel_server_id?.toString() || "",
      name: client.name || "",
      extension: client.extension || "",
      secret: client.secret || "",
      external_id: client.external_id || "",
      cnic: client.cnic || "",
      address: client.address || "",
      li_enabled: client.li_enabled || false,
      voicemail_enabled: client.voicemail_enabled || false,
    });
    setShowSecret(false);
    setModalOpen(true);
  };

  // Submit form
  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!form.issabel_server_id) {
      toast.error("Please select an Issabel server");
      return;
    }

    setSubmitting(true);

    const method = selected ? "PUT" : "POST";
    const url = selected
      ? `${API_BASE_URL}/voice/clients/${selected.id}`
      : `${API_BASE_URL}/voice/clients`;

    const payload = {
      issabel_server_id: parseInt(form.issabel_server_id),
      name: form.name.trim(),
      ...(form.extension && { extension: form.extension.trim() }),
      ...(form.secret && { secret: form.secret.trim() }),
      ...(form.external_id && { external_id: form.external_id.trim() }),
      ...(form.cnic && { cnic: form.cnic.trim() }),
      ...(form.address && { address: form.address.trim() }),
      li_enabled: form.li_enabled,
      voicemail_enabled: form.voicemail_enabled,
    };

    try {
      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Action failed");
      }

      toast.success(`Client ${selected ? "updated" : "created"} successfully`);
      setModalOpen(false);
      loadClients();
    } catch (err) {
      toast.error(err.message || "Action failed");
    } finally {
      setSubmitting(false);
    }
  };

  // Delete client
  const confirmDelete = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/voice/clients/${selected.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Delete failed");
      }

      toast.success("Client deleted");
      setDeleteConfirm(false);
      setSelected(null);
      loadClients();
    } catch (err) {
      toast.error(err.message || "Delete failed");
    }
  };

  // Toggle LI (Lawful Intercept / Recording)
  const toggleLI = async (client) => {
    const action = client.li_enabled ? "disable" : "enable";

    try {
      const res = await fetch(
        `${API_BASE_URL}/voice/clients/${client.id}/li/${action}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || `Failed to ${action} LI`);
      }

      toast.success(`Lawful Intercept ${action}d for ${client.extension}`);
      loadClients();
    } catch (err) {
      toast.error(err.message || `Failed to ${action} Lawful Intercept`);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Phone className="text-blue-600" /> Voice Clients
          </h2>
          <div className="flex items-center gap-3">
            <button
              onClick={loadClients}
              disabled={loading}
              className="p-2 rounded-lg border hover:bg-gray-50 transition"
              title="Refresh"
            >
              <RefreshCcw
                size={18}
                className={loading ? "animate-spin text-blue-600" : "text-gray-600"}
              />
            </button>
            <button
              onClick={openAddModal}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow flex items-center gap-2 transition"
            >
              <Plus size={18} /> Add Client
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white border rounded-xl p-4 mb-6 shadow-sm">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={handleSearch}
              placeholder="Search by name, extension, or CNIC..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto bg-white rounded-xl shadow border">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-100 text-gray-600 text-xs uppercase border-b">
              <tr>
                <th className="px-4 py-3">Extension</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">CNIC</th>
                <th className="px-4 py-3">Server</th>
                <th className="px-4 py-3 text-center">LI</th>
                <th className="px-4 py-3 text-center">Voicemail</th>
                <th className="px-4 py-3 text-center">Synced</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-gray-500">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Loading clients...
                  </td>
                </tr>
              ) : clients.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-gray-500">
                    <Phone className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    No voice clients found
                  </td>
                </tr>
              ) : (
                clients.map((client) => (
                  <tr key={client.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="font-mono font-semibold text-blue-600">
                        {client.extension}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-800">
                      {client.name}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {client.cnic || "-"}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {client.issabel_server?.name || "-"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => toggleLI(client)}
                        className={`p-2 rounded-full transition ${
                          client.li_enabled
                            ? "bg-red-100 text-red-600 hover:bg-red-200"
                            : "bg-gray-100 text-gray-400 hover:bg-gray-200"
                        }`}
                        title={client.li_enabled ? "Disable Lawful Intercept" : "Enable Lawful Intercept"}
                      >
                        {client.li_enabled ? (
                          <Mic className="w-4 h-4" />
                        ) : (
                          <MicOff className="w-4 h-4" />
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {client.voicemail_enabled ? (
                        <Voicemail className="w-5 h-5 text-blue-500 mx-auto" />
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {client.issabel_synced ? (
                        <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                      ) : (
                        <AlertCircle
                          className="w-5 h-5 text-amber-500 mx-auto"
                          title={client.last_sync_error || "Not synced"}
                        />
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => openEditModal(client)}
                          className="p-2 rounded-lg hover:bg-blue-50 text-blue-600 transition"
                          title="Edit"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => {
                            setSelected(client);
                            setDeleteConfirm(true);
                          }}
                          className="p-2 rounded-lg hover:bg-red-50 text-red-600 transition"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center mt-6">
            <div className="text-sm text-gray-500">
              Showing {clients.length} of {totalCount} clients
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1 || loading}
                className="flex items-center gap-1 px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                <ArrowLeft size={16} /> Previous
              </button>
              <span className="text-sm text-gray-600 px-4">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage >= totalPages || loading}
                className="flex items-center gap-1 px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Next <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Add/Edit Modal */}
        <Transition appear show={modalOpen} as={Fragment}>
          <Dialog
            as="div"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            onClose={() => !submitting && setModalOpen(false)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
                <Dialog.Title className="text-xl font-bold mb-6 text-gray-800 flex items-center gap-2">
                  {selected ? (
                    <Pencil className="text-blue-600" />
                  ) : (
                    <Plus className="text-blue-600" />
                  )}
                  {selected ? "Edit Voice Client" : "Add Voice Client"}
                </Dialog.Title>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit();
                  }}
                  className="space-y-6"
                >
                  {/* Server Selection */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-1">
                      <Server className="w-4 h-4 text-gray-500" />
                      Issabel Server <span className="text-red-500">*</span>
                    </label>
                    {loadingServers ? (
                      <div className="flex items-center gap-2 text-gray-500 py-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading servers...
                      </div>
                    ) : (
                      <select
                        value={form.issabel_server_id}
                        onChange={(e) =>
                          setForm({ ...form, issabel_server_id: e.target.value })
                        }
                        disabled={!!selected}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      >
                        <option value="">Select a server...</option>
                        {servers.map((server) => (
                          <option key={server.id} value={server.id}>
                            {server.name} ({server.host})
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Name */}
                    <div className="md:col-span-2">
                      <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-1">
                        <User className="w-4 h-4 text-gray-500" />
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="John Doe"
                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>

                    {/* Extension */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-1">
                        <Phone className="w-4 h-4 text-gray-500" />
                        Extension
                      </label>
                      <input
                        type="text"
                        value={form.extension}
                        onChange={(e) =>
                          setForm({ ...form, extension: e.target.value })
                        }
                        placeholder="1001 (auto if empty)"
                        disabled={!!selected}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      />
                    </div>

                    {/* Secret */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-1">
                        <Key className="w-4 h-4 text-gray-500" />
                        SIP Password
                      </label>
                      <div className="relative">
                        <input
                          type={showSecret ? "text" : "password"}
                          value={form.secret}
                          onChange={(e) =>
                            setForm({ ...form, secret: e.target.value })
                          }
                          placeholder="Auto-generate if empty"
                          className="w-full border border-gray-300 rounded-lg px-4 py-2.5 pr-10 focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSecret(!showSecret)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showSecret ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>

                    {/* External ID */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-1">
                        <FileText className="w-4 h-4 text-gray-500" />
                        External ID
                      </label>
                      <input
                        type="text"
                        value={form.external_id}
                        onChange={(e) =>
                          setForm({ ...form, external_id: e.target.value })
                        }
                        placeholder="Customer ID"
                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* CNIC */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-1">
                        <CreditCard className="w-4 h-4 text-gray-500" />
                        CNIC
                      </label>
                      <input
                        type="text"
                        value={form.cnic}
                        onChange={(e) => setForm({ ...form, cnic: e.target.value })}
                        placeholder="12345-1234567-1"
                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Address */}
                    <div className="md:col-span-2">
                      <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-1">
                        <MapPin className="w-4 h-4 text-gray-500" />
                        Address
                      </label>
                      <textarea
                        value={form.address}
                        onChange={(e) =>
                          setForm({ ...form, address: e.target.value })
                        }
                        placeholder="Full address"
                        rows={2}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 resize-none"
                      />
                    </div>
                  </div>

                  {/* Features */}
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={form.li_enabled}
                        onChange={(e) =>
                          setForm({ ...form, li_enabled: e.target.checked })
                        }
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <Mic className="w-5 h-5 text-red-500" />
                      <div>
                        <span className="font-medium">Enable Lawful Intercept</span>
                        <p className="text-xs text-gray-500">
                          Record all calls for this extension
                        </p>
                      </div>
                    </label>

                    <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={form.voicemail_enabled}
                        onChange={(e) =>
                          setForm({ ...form, voicemail_enabled: e.target.checked })
                        }
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <Voicemail className="w-5 h-5 text-blue-500" />
                      <div>
                        <span className="font-medium">Enable Voicemail</span>
                        <p className="text-xs text-gray-500">
                          Allow callers to leave voicemail
                        </p>
                      </div>
                    </label>
                  </div>

                  {/* Buttons */}
                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <button
                      type="button"
                      onClick={() => setModalOpen(false)}
                      disabled={submitting}
                      className="px-4 py-2 rounded-lg border text-gray-600 hover:bg-gray-100 flex items-center gap-1"
                    >
                      <XCircle size={18} />
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="px-5 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
                    >
                      {submitting ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <Save size={18} />
                      )}
                      {selected ? "Update Client" : "Create Client"}
                    </button>
                  </div>
                </form>
              </div>
            </Transition.Child>
          </Dialog>
        </Transition>

        {/* Delete Confirmation */}
        <Transition appear show={deleteConfirm} as={Fragment}>
          <Dialog
            as="div"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            onClose={() => setDeleteConfirm(false)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
                <Dialog.Title className="text-lg font-bold text-gray-800 mb-4">
                  Delete Voice Client
                </Dialog.Title>
                <p className="text-gray-600 mb-6">
                  Are you sure you want to delete{" "}
                  <span className="font-semibold">{selected?.name}</span>{" "}
                  (Extension: {selected?.extension})? This will also remove the
                  extension from Issabel PBX.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setDeleteConfirm(false)}
                    className="px-4 py-2 rounded-lg border text-gray-600 hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </Transition.Child>
          </Dialog>
        </Transition>
      </div>
    </DashboardLayout>
  );
}